/**
 * Shared print utilities for thermal label rolls.
 *
 * Every label print flow (GRN serials, item barcodes, location tags,
 * opening-stock strips) uses these two helpers so they all:
 *   • Set @page size to the exact label dimensions (so one physical sticker
 *     = one page, and the printer's gap sensor advances the roll cleanly).
 *   • Emit properly-nested <head> / <body> HTML (bad nesting broke
 *     page-break-after in earlier versions and stacked labels on one page).
 *   • Include modern `break-after: page` alongside legacy
 *     `page-break-after: always` so every print engine cooperates.
 *
 * Callers supply the per-cell markup (`<div class="cell">…</div>`) but
 * MUST NOT include their own `<style>` block for the cell layout — that
 * skeleton lives here.
 */

export interface LabelPrintOptions {
  /** Label width in mm (matches the physical sticker on the roll). */
  w: number;
  /** Label height in mm. */
  h: number;
  /** Padding inside each label. Defaults to 4/2/2/2 (left has larger default
   *  to compensate for the typical thermal-printhead dead zone). */
  padding?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Window title. */
  title?: string;
  /** Extra CSS appended after the shared skeleton — for label-type-specific
   *  classes like `.item-name`, `.loc-code`, etc. */
  extraCss?: string;
  /** Rotate label content clockwise by N degrees. 0 (default), 90, 180, or
   *  270. The physical @page size stays w×h — only the content inside the
   *  cell rotates, so it still prints one label per sticker. */
  rotate?: 0 | 90 | 180 | 270;
  /** Stickers across the roll. 2 = a 2-up roll (e.g. 100×25 holding two
   *  50×25 stickers): w×h is ONE sticker, each page is (w × across) × h and
   *  every label is printed on all stickers of its row. Default 1. */
  across?: 1 | 2;
}

const clampPad = (v: number | undefined, fallback: number) =>
  Math.max(0, Math.min(15, typeof v === 'number' && !Number.isNaN(v) ? v : fallback));

/**
 * Build the full HTML document for a label-print window. `cellsHtml` is the
 * concatenation of one `<div class="cell">…</div>` per label. Each cell
 * gets its own @page via `break-after: page`, so N cells → N pages → N
 * physical stickers.
 */
export function buildLabelPrintHtml(opts: LabelPrintOptions & { cellsHtml: string }): string {
  const { w, h, cellsHtml, extraCss = '', rotate = 0, across = 1 } = opts;
  const pageW = w * across;
  // Always stamp the dimensions into the window title so the print preview
  // header makes it obvious what page size we asked the printer for.
  const title = `${opts.title ?? 'Labels'} · ${across > 1 ? `${across} × ` : ''}${w}×${h} mm${rotate ? ` · rot ${rotate}°` : ''}`;
  const padT = clampPad(opts.padding?.top, 2);
  const padR = clampPad(opts.padding?.right, 3);
  const padB = clampPad(opts.padding?.bottom, 2);
  const padL = clampPad(opts.padding?.left, 3);
  // Portrait label rolls (tall + narrow, e.g. 40×60) can't fit the landscape
  // info-left / QR-right layout — the horizontal split would squeeze both to
  // nothing. When the sticker is taller than wide we stack the QR on top of
  // the info block instead, using vertical flex, and grow the QR so it uses
  // the width. Landscape labels keep the original side-by-side layout.
  // When rotated 90/270, design the content for the SWAPPED dimensions
  // (contentW × contentH) — the CSS transform below rotates it into the
  // physical @page (w × h). Layout decisions (portrait vs landscape) use
  // the swapped dims so text still reads right on the rotated sticker.
  const rotated = rotate === 90 || rotate === 270;
  const contentW = rotated ? h : w;
  const contentH = rotated ? w : h;
  const isPortrait = contentH > contentW;
  const topRowFlex = isPortrait
    ? `.top-row { display: flex; flex-direction: column-reverse; gap: 0.8mm; flex: 1; min-height: 0; align-items: stretch; justify-content: center; }
       .info { flex: 0 0 auto; display: flex; flex-direction: column; gap: 0.2mm; min-width: 0; overflow: hidden; text-align: center; align-items: center; }
       .qr-block { flex-shrink: 0; display: flex; flex-direction: column; align-items: center; gap: 0.2mm; }`
    : `.top-row { display: flex; flex-direction: row; gap: 0.8mm; flex: 1; min-height: 0; align-items: center; }
       .info { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 0.2mm; min-width: 0; overflow: hidden; }
       .qr-block { flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.2mm; }`;

  // Rotation is applied at the .cell-inner level. The outer .cell is a
  // fixed WxH container that matches the @page; .cell-inner is drawn at
  // the swapped dimensions and rotated so its bounding box lands exactly
  // inside the outer .cell. Origin math for CW 90°: rotate around top-
  // left, then translate right by W to bring it into view.
  const innerTransform = rotate === 90
    ? `transform-origin: 0 0; transform: translate(${w}mm, 0) rotate(90deg);`
    : rotate === 270
      ? `transform-origin: 0 0; transform: translate(0, ${h}mm) rotate(-90deg);`
      : rotate === 180
        ? `transform-origin: center center; transform: rotate(180deg);`
        : '';

  // Split cells, filter empty chunks, wrap each in .cell-inner.
  const cellParts = cellsHtml
    .split('<div class="cell">')
    .filter((p) => p.trim().length > 0);

  const wrappedCells = cellParts
    .map((part) => {
      const lastClose = part.lastIndexOf('</div>');
      if (lastClose === -1) return part;
      const inner = part.slice(0, lastClose);
      const rest = part.slice(lastClose);
      const cell = `<div class="cell"><div class="cell-inner">${inner}</div>${rest}`;
      // 2-up roll: the same label on every sticker of the row.
      return across > 1 ? `<div class="row">${cell.repeat(across)}</div>` : cell;
    })
    .join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  /* One physical sticker = one page: the @page is exactly the label size and
     every .cell forces a break, so N labels → N pages and the printer's gap
     sensor advances the roll between stickers. Never a multi-label page. */
  @page { size: ${pageW}mm ${h}mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: ${pageW}mm; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #000; }
  .cell {
    display: block;
    position: relative;
    width: ${w}mm;
    height: ${h}mm;
    overflow: hidden;
    page-break-after: always;
    break-after: page;
  }
  .cell:last-child { page-break-after: auto; break-after: auto; }
  ${across > 1 ? `/* 2-up: the row is the page; its stickers sit side by side. */
  .row { display: flex; width: ${pageW}mm; height: ${h}mm; overflow: hidden; page-break-after: always; break-after: page; }
  .row:last-child { page-break-after: auto; break-after: auto; }
  .row .cell { flex: 0 0 ${w}mm; page-break-after: auto; break-after: auto; }` : ''}
  .cell-inner {
    ${rotated
      ? `position: absolute; top: 0; left: 0; width: ${contentW}mm; height: ${contentH}mm; ${innerTransform}`
      : `width: 100%; height: 100%;`}
    padding: ${padT}mm ${padR}mm ${padB}mm ${padL}mm;
    display: flex;
    flex-direction: column;
    gap: 1.5mm;
    overflow: hidden;
  }
  ${topRowFlex}
  .barcode-row {
    flex-shrink: 0;
    width: 100%;
    text-align: center;
    border-top: 0.3mm solid rgba(0,0,0,0.12);
    padding-top: 1mm;
  }
  .barcode-row img { width: 100%; display: block; }
  ${extraCss}
</style>
</head>
<body>
${wrappedCells}
<script>window.onload = () => window.print();<\/script>
</body>
</html>`;
}

/**
 * Prints ONE calibration label at the given dimensions with corner
 * crosshairs and a mm ruler along the top edge. Operators hold this
 * against a physical sticker to confirm the size matches before printing
 * a whole batch — the fastest way to catch a mismatched paper-size setup
 * without wasting a roll.
 */
export function buildCalibrationHtml(opts: LabelPrintOptions): string {
  const { w, h, across = 1 } = opts;
  const title = `${opts.title ?? 'Calibration'} · ${across > 1 ? `${across} × ` : ''}${w}×${h} mm`;
  const tickCountX = Math.floor(w / 5);
  const tickCountY = Math.floor(h / 5);
  const xTicks = Array.from({ length: tickCountX + 1 }, (_, i) => `
    <div style="position:absolute; left:${i * 5}mm; top:0; width:0.15mm; height:${i % 2 === 0 ? '2mm' : '1mm'}; background:#000;"></div>
    ${i % 2 === 0 ? `<div style="position:absolute; left:${i * 5 - 1.5}mm; top:2.2mm; font-size:5pt; font-family:monospace;">${i * 5}</div>` : ''}
  `).join('');
  const yTicks = Array.from({ length: tickCountY + 1 }, (_, i) => `
    <div style="position:absolute; top:${i * 5}mm; left:0; height:0.15mm; width:${i % 2 === 0 ? '2mm' : '1mm'}; background:#000;"></div>
  `).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: ${w * across}mm ${h}mm${across > 1 ? '' : ' portrait'}; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: ${w * across}mm; margin: 0; padding: 0; }
  .cal-row { display: flex; }
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #000; }
  .cal { position: relative; width: ${w}mm; height: ${h}mm; border: 0.3mm dashed #000; overflow: hidden; }
  .center-text { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
  .corner { position: absolute; width: 4mm; height: 4mm; background: transparent; }
  .corner::before, .corner::after { content: ''; position: absolute; background: #000; }
  .corner::before { top: 50%; left: 0; right: 0; height: 0.3mm; transform: translateY(-50%); }
  .corner::after  { left: 50%; top: 0; bottom: 0; width: 0.3mm; transform: translateX(-50%); }
</style>
</head>
<body>
<div class="cal-row">${`<div class="cal">
  <div class="corner" style="top:0;left:0;"></div>
  <div class="corner" style="top:0;right:0;"></div>
  <div class="corner" style="bottom:0;left:0;"></div>
  <div class="corner" style="bottom:0;right:0;"></div>
  ${xTicks}
  ${yTicks}
  <div class="center-text">
    <div style="font-size:${Math.max(8, Math.min(w, h) * 0.35).toFixed(1)}pt; font-weight:900;">${w} × ${h} mm</div>
    <div style="font-size:${Math.max(5, Math.min(w, h) * 0.15).toFixed(1)}pt; margin-top:1mm;">Calibration Test</div>
    <div style="font-size:${Math.max(4, Math.min(w, h) * 0.12).toFixed(1)}pt; margin-top:0.8mm; color:#555;">
      Should fill exactly one sticker
    </div>
  </div>
</div>`.repeat(across)}</div>
<script>window.onload = () => window.print();<\/script>
</body>
</html>`;
}

/**
 * Open a print window pre-sized to the label and write the given HTML.
 * Returns the window (or null when the popup was blocked).
 */
export function openPrintWindow(html: string, w: number, h: number): Window | null {
  const win = window.open('', '_blank', `width=${Math.max(240, w * 4)},height=${Math.max(160, h * 4)}`);
  if (!win) return null;
  win.document.write(html);
  win.document.close();
  return win;
}

/**
 * Common preset sizes for thermal label rolls.
 */
export const THERMAL_LABEL_PRESETS = [
  { key: '100x50', label: '100 × 50 mm', w: 100, h: 50 },
  { key: '100x25', label: '100 × 25 mm', w: 100, h: 25 },
  { key: '80x50',  label: '80 × 50 mm',  w: 80,  h: 50 },
  { key: '60x40',  label: '60 × 40 mm',  w: 60,  h: 40 },
  { key: '60x30',  label: '60 × 30 mm',  w: 60,  h: 30 },
  { key: '50x30',  label: '50 × 30 mm',  w: 50,  h: 30 },
  { key: '50x25',  label: '50 × 25 mm',  w: 50,  h: 25 },
  { key: '40x25',  label: '40 × 25 mm',  w: 40,  h: 25 },
] as const;

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
